import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, MenuItem, TableBody, TableCell, TableRow, ThemeProvider, Toolbar, Typography, createTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { NumericFormat } from "react-number-format";
import moment from "moment";
import Moment from "react-moment";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import Loader from "../../../shared/components/Loader";
import TableEmpty from "../../../shared/components/tables/TableEmpty";
import BoxSearchIcon from "../../../shared/assets/icons/BoxSearchIcon";
import Table from "../../../shared/components/tables/Table";
import ActionToast from "../../../shared/components/ActionToast";
import { NotificationMessage, formatWarrantyFullName, getName } from "../../../shared/models/models";
import TablePagination from "../../../shared/components/tables/TablePagination";
import { ProductAvailabilityCode } from "../../shared/models/customerBuyerStockItemsModels";
import colors from "../../../shared/themes/colors";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import PresetChip from "../../../shared/components/PresetChip";
import { productCondition } from "../../../common/models/CommonModels";
import { CustomerSentOffersState, Offer, OfferStatusCode, OffersQuery, OffersQueryParameter, isOffersQueryEmpty } from "../models/models";
import { cancelOffer, changeQuery, getOffers, setError, setSuccess } from "../customerSentOffersSlice";
import useTranslation from "../../../shared/hooks/useTranslation";

const getDeliveryTerm = (availabilityCode: ProductAvailabilityCode) => {
  switch (availabilityCode) {
    case ProductAvailabilityCode.inStock:
      return `Ship by ${moment().format("D.M.yyyy")}`;
    case ProductAvailabilityCode.in7Days:
      return "Ready in 7 days";
    case ProductAvailabilityCode.in14Days:
      return "Ready in 14 days";
    case ProductAvailabilityCode.in1Month:
      return "Ready in 1 month";
  }
};

enum StatusPresetCode {
  all = 0,
  accepted = 1,
}

const statusPresets = [
  {
    label: "All Offers",
    value: StatusPresetCode.all
  },
  {
    label: "Accepted",
    value: StatusPresetCode.accepted
  }
];

const SentOffers = () => {
  const { query, offers, loading, success, error } = useAppSelector<CustomerSentOffersState>(
    (state: any) => state.customerSentOffers
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const { translate } = useTranslation("common");

  const columns: TableColumnDefinition[] = [
    { title: "Buyer company info" },
    { title: "Product details" },
    { title: "Price/pc, condition" },
    { title: "Shipping" },
    { title: "RFQ details", parameter: OffersQueryParameter.createdDate },
    { title: undefined }
  ];

  const [statusPresetCode, setStatusPresetCode] = useState(StatusPresetCode.all);

  const handleGoToRfqFeed = () => {
    navigate(`/trading/rfq-feed`);
  };

  const handleGoToRfq = (offer: Offer) => {
    if (offer) {
      navigate(`/trading/rfq-feed`);
    }
  };

  const canCloseOffer = (offer: Offer) => {
    return offer.statusCode === OfferStatusCode.new ||
      offer.statusCode === OfferStatusCode.seen ||
      offer.statusCode === OfferStatusCode.accepted;
  };

  const handleStatusPresetChange = (code: StatusPresetCode) => {
    setStatusPresetCode(code);

    const statusCode = code === StatusPresetCode.all ? undefined : OfferStatusCode.accepted;
    const newQuery = { ...query, statusCode: statusCode };
    dispatch(changeQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const handleQueryChange = (value: OffersQuery) => {
    dispatch(changeQuery(value));
    dispatch(getOffers(value));
  };

  const handleSort = (parameter: number) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    const newQuery = { ...query, sortBy: parameter, sortDesc: sortDesc };

    dispatch(changeQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const handlePageSizeChange = async (size: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: size };
    await dispatch(changeQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const handlePageNoChange = async (no: number) => {
    const newQuery = { ...query, pageNo: no };
    await dispatch(changeQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const handleCancelOffer = async (offer: Offer) => {
    await dispatch(cancelOffer(offer.id));
    await dispatch(getOffers(query));
  };

  const GoToRfqFeedButton = () => {
    return (
      <Button variant="contained" onClick={handleGoToRfqFeed}>
        Go to RFQ Feed
      </Button>
    );
  };

  const isQueryEmpty = isOffersQueryEmpty(query);

  const init = () => {
    dispatch(getOffers(query));
  };

  useEffect(() => {
    init();
  }, []);

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9"
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <Toolbar className="flex items-center justify-between">
        <Box className="flex items-center">
          <Typography fontSize={24} fontWeight={500} className="heading-2">
            Offers Sent
          </Typography>

          <Box className="ml-16 flex gap-4">
            {statusPresets.map((preset) => (
              <PresetChip
                key={preset.value}
                label={preset.label}
                active={statusPresetCode === preset.value}
                onClick={() => handleStatusPresetChange(preset.value)} />
            ))}
          </Box>
        </Box>

        <GoToRfqFeedButton />
      </Toolbar>

      <Box className="px-4 v-fill">
        {loading ? (
          <Loader />
        ) : !offers?.items.length ? (
          <TableEmpty
            iconComponent={BoxSearchIcon}
            text={isQueryEmpty ? "You have no offers yet" : "You don’t have such offers"}
            secondaryText={isQueryEmpty ? "First choose RFQ you would like to send offer to" : undefined}
          >
            {isQueryEmpty && <GoToRfqFeedButton />}
          </TableEmpty>
        ) : (
          <Table>
            <SortTableHead columns={columns} sortBy={query.sortBy} sortDesc={query.sortDesc} onSort={handleSort} />

            <TableBody>
              {offers?.items.map((item) => {
                return (
                  <TableRow key={item.id}>
                    <TableCell className="break-all" sx={{ maxWidth: "250px" }}>
                      <Box className="text-14-bold" color={"#333333"}>
                        {item.buyer.name}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ maxWidth: "200px" }}>
                      <Box className="block break-all">
                        <Box>
                          <Box className="text-14-medium" color={"#333333"}>
                            {item.productNumber}
                          </Box>
                          <Box className="text-12-medium" color={"#747881"}>
                            {item.productAlternativeNumber}
                          </Box>
                        </Box>

                        <Box className="mt-2">
                          <Box className="text-12-medium" color={"#333333"}>
                            {item.manufacturer.name}
                          </Box>
                          <Box className="text-12-regular" color={"#747881"}>
                            {item.category.name}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box className="text-14-medium" color={"#333333"}>
                        {translate(`productCondition.${productCondition(item.productConditionCode)}`)}
                      </Box>

                      <Box className="text-18-medium" color={"#333333"}>
                        <NumericFormat
                          value={item.unitPrice?.value}
                          displayType="text"
                          thousandSeparator=" "
                          suffix="€" />/pc
                      </Box>

                      <Box className="text-12-regular" color={"#333333"}>
                        {formatWarrantyFullName(item.warrantyPeriodMonths)}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box className="text-14-medium" color={colors.secondary.gray20}>
                        {getDeliveryTerm(item.availabilityCode)}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Box className="text-12-regular" color={colors.secondary.gray40}>
                          Created on{"\u00A0"}
                          <Moment date={item.createdDate} format="DD.MM.YYYY" />
                        </Box>

                        <Box className="text-12-regular" color={colors.secondary.gray40}>
                          {"#" + item.quotationRequestNumber}
                        </Box>

                        <Button variant="text" onClick={() => handleGoToRfq(item)}>
                          Go to RFQ
                        </Button>
                      </Box>
                    </TableCell>

                    <TableCell>
                      {canCloseOffer(item) && (
                        <TableActionButton stopPropagation={true}>
                          <MenuItem
                            onClick={() =>
                              confirm({
                                title: "Close offer",
                                text: `Are you sure you want to cancel ${getName(
                                  item.manufacturer.name,
                                  item.productNumber
                                )} offer?`,
                                confirmButton: "Yes, cancel",
                                closeButton: "No",
                                severity: "error",
                                onConfirm: () => handleCancelOffer(item)
                              })
                            }
                          >
                            <CloseIcon />
                            Cancel offer
                          </MenuItem>
                        </TableActionButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>

      <TablePagination
        variant="bottom"
        pageNo={query.pageNo}
        pageSize={query.pageSize}
        totalCount={offers?.totalCount}
        onPageNoChange={handlePageNoChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </ThemeProvider>
  );
};

export default SentOffers;